// theme-yaml.js - the board's theme as text in the YAML buffer.
//
// The theme selector reads and writes the top-level `theme:` key (or its
// `extends:` spelling) directly in the editor text. Edits are minimal change
// specs rather than a parse-and-dump round trip, so comments, ordering and
// formatting elsewhere in the document survive.

const THEME_KEY_LINE = /^(theme|extends):[ \t]*(.*)$/gm;
const TOP_LEVEL_KEY = /^[A-Za-z_][\w-]*:/m;

function splitComment(rest) {
    const at = rest.search(/(^|\s)#/);
    return at === -1 ? [rest.trim(), ''] : [rest.slice(0, at).trim(), rest.slice(at).trim()];
}

function unquote(value) {
    return value.replace(/^(["'])(.*)\1$/, '$2');
}

// A one-line plain or quoted scalar. Empty means the value is a block
// sequence/mapping on the following lines; `[ { | > & * !` open a flow
// collection, block scalar, anchor, alias or tag. None can be replaced by
// rewriting the key line alone.
function isScalar(raw) {
    return raw !== '' && !/^[[{|>&*!]/.test(raw);
}

/**
 * The first top-level `theme:`/`extends:` as `{ key, value, scalar }`; null if
 * neither. `scalar` is false when ANY such key's value is not a one-line
 * scalar, so callers lock on, and `themeChanges` refuses, the same documents.
 */
export function readThemeKey(yaml) {
    const matches = [...yaml.matchAll(THEME_KEY_LINE)];
    if (matches.length === 0) return null;
    const raw = splitComment(matches[0][2])[0];
    return {
        key: matches[0][1],
        value: unquote(raw),
        scalar: matches.every((m) => isScalar(splitComment(m[2])[0])),
    };
}

// Above the first top-level key and the comment lines attached to it, so a
// file-header comment (separated by a blank line) stays on top.
function insertionPoint(yaml) {
    const first = TOP_LEVEL_KEY.exec(yaml);
    if (!first) return yaml.length;
    let at = first.index;
    while (at > 0) {
        const previousLine = yaml.lastIndexOf('\n', at - 2) + 1;
        if (!yaml.startsWith('#', previousLine)) break;
        at = previousLine;
    }
    return at;
}

/**
 * CodeMirror change specs (positions refer to `yaml`) that make `theme` the
 * board's one theme key: the first `theme:`/`extends:` line becomes
 * `theme: <theme>` (trailing comment kept), any further ones are dropped so
 * the compiler never sees both, and a board with neither gets the key as its
 * first top-level key. Returns no changes when a matched key's value is not a
 * one-line scalar: rewriting only the key line would orphan its value. An
 * empty `theme` removes every theme key so the board inherits.
 */
export function themeChanges(yaml, theme) {
    const matches = [...yaml.matchAll(THEME_KEY_LINE)];
    if (matches.length > 0 && !readThemeKey(yaml).scalar) return [];
    if (matches.length === 0 && theme === '') return [];
    if (matches.length === 0) {
        const at = insertionPoint(yaml);
        const lead = at === yaml.length && yaml && !yaml.endsWith('\n') ? '\n' : '';
        return [{ from: at, to: at, insert: `${lead}theme: ${theme}\n` }];
    }
    return matches.map((match, i) => {
        const from = match.index;
        if (i > 0 || theme === '') {
            const next = yaml.indexOf('\n', from);
            return { from, to: next === -1 ? yaml.length : next + 1, insert: '' };
        }
        const comment = splitComment(match[2])[1];
        return {
            from,
            to: from + match[0].length,
            insert: `theme: ${theme}${comment ? `  ${comment}` : ''}`,
        };
    });
}
